"use client";

import { useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { type Locale } from "@/config/locale";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { switchChannelPath } from "@/lib/geo/switch-channel-path";
import { AccordionPanel } from "./accordion-panel";
import { LanguageDetailPanel } from "./language-detail-panel";
import { RegionDetailPanel } from "./region-detail-panel";
import { CurrencyDetailPanel } from "./currency-detail-panel";

type View = "summary" | "language" | "region" | "currency";

export interface LocaleChannelAccordionProps {
	currentLocale: Locale;
	currentChannel: string;
	currentCurrency: string;
	/** Called after any selection so the parent can close the sheet/popover. */
	onClose?: () => void;
}

export function LocaleChannelAccordion({
	currentLocale,
	currentChannel,
	currentCurrency,
	onClose,
}: LocaleChannelAccordionProps) {
	const router = useRouter();
	const pathname = usePathname();
	const [view, setView] = useState<View>("summary");

	const switchLocale = (locale: Locale) => {
		setGeoPrefCookie({ locale, channel: currentChannel, referenceCurrency: currentCurrency });
		const next = pathname.replace(`/${currentLocale}/`, `/${locale}/`);
		router.push(next);
		onClose?.();
	};

	const switchChannel = (channel: "eu" | "us") => {
		setGeoPrefCookie({ locale: currentLocale, channel, referenceCurrency: currentCurrency });
		router.push(switchChannelPath(pathname, channel));
		onClose?.();
	};

	const switchCurrency = (code: string) => {
		setGeoPrefCookie({ locale: currentLocale, channel: currentChannel, referenceCurrency: code });
		router.refresh();
		onClose?.();
	};

	if (view === "language") {
		return (
			<LanguageDetailPanel
				currentLocale={currentLocale}
				onBack={() => setView("summary")}
				onSelect={switchLocale}
			/>
		);
	}

	if (view === "region") {
		return (
			<RegionDetailPanel
				currentChannel={currentChannel}
				onBack={() => setView("summary")}
				onSelect={switchChannel}
			/>
		);
	}

	if (view === "currency") {
		return (
			<CurrencyDetailPanel
				currentLocale={currentLocale}
				currentCurrency={currentCurrency}
				onBack={() => setView("summary")}
				onSelect={switchCurrency}
			/>
		);
	}

	return (
		<AccordionPanel
			currentLocale={currentLocale}
			currentChannel={currentChannel}
			currentCurrency={currentCurrency}
			onOpenLanguage={() => setView("language")}
			onOpenRegion={() => setView("region")}
			onOpenCurrency={() => setView("currency")}
		/>
	);
}
